import { FaceDetectionService } from "./FaceDetectionService"

export interface AttendanceRecord {
  id: string
  type: "entry" | "exit"
  timestamp: string
  location: {
    latitude: number
    longitude: number
  }
  facesDetected?: number
  faces?: any[]
  punchCode: string
  backendResponse?: any
  backendSuccess?: boolean
}

export class AttendanceService {
  private static STORAGE_KEY = "attendance_records"

  static async markEntry(location: any, faces: any[], punchCode: string, imageBase64?: string): Promise<{ record: AttendanceRecord; backendResponse: any }> {
    try {
      if (!location || !location.coords) {
        throw new Error("Invalid location data")
      }

      if (!punchCode || punchCode.trim() === "") {
        throw new Error("Punch code is required")
      }

      console.log("AttendanceService: Marking entry with backend...")
      console.log("AttendanceService: Using punch code:", punchCode)

      // Use the face detection result that was already processed
      const record: AttendanceRecord = {
        id: Date.now().toString(),
        type: "entry",
        timestamp: new Date().toISOString(),
        location: {
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        },
        facesDetected: faces.length,
        faces: faces,
        punchCode: punchCode,
        backendSuccess: true,
      }

      // Store locally
      const existingRecords = await this.getAttendanceHistory()
      const updatedRecords = [record, ...existingRecords]

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedRecords))
      console.log("AttendanceService: Entry record saved locally")

      // Return record and a simple success response
      return {
        record,
        backendResponse: {
          status: "success",
          message: "Attendance marked successfully",
        },
      }
    } catch (error) {
      console.error("AttendanceService: Error marking entry:", error)
      throw error
    }
  }

  static async markExit(location: any, punchCode: string): Promise<{ record: AttendanceRecord; backendResponse: any }> {
    try {
      if (!location || !location.coords) {
        throw new Error("Invalid location data")
      }

      if (!punchCode || punchCode.trim() === "") {
        throw new Error("Punch code is required")
      }

      console.log("AttendanceService: Marking exit...")
      console.log("AttendanceService: Using punch code:", punchCode)

      // Send exit data to backend
      const backendResult = await FaceDetectionService.markExit(
        punchCode,
        location.coords.latitude,
        location.coords.longitude,
      )
      console.log("AttendanceService: Backend exit response:", backendResult)

      const record: AttendanceRecord = {
        id: Date.now().toString(),
        type: "exit",
        timestamp: new Date().toISOString(),
        location: {
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        },
        punchCode: punchCode,
        backendResponse: backendResult,
        backendSuccess: true,
      }

      // Store locally
      const existingRecords = await this.getAttendanceHistory()
      const updatedRecords = [record, ...existingRecords]

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedRecords))
      console.log("AttendanceService: Exit record saved locally")

      // Return both record and backend response for UI display
      return { record, backendResponse: backendResult }
    } catch (error) {
      console.error("AttendanceService: Error marking exit:", error)
      throw error
    }
  }

  static async getAttendanceHistory(): Promise<AttendanceRecord[]> {
    try {
      const records = localStorage.getItem(this.STORAGE_KEY)
      return records ? JSON.parse(records) : []
    } catch (error) {
      console.error("AttendanceService: Error getting attendance history:", error)
      return []
    }
  }

  static async clearHistory(): Promise<void> {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
    } catch (error) {
      console.error("AttendanceService: Error clearing history:", error)
      throw error
    }
  }

  static async getTodayAttendance(): Promise<AttendanceRecord[]> {
    try {
      const allRecords = await this.getAttendanceHistory()
      const today = new Date().toDateString()

      return allRecords.filter((record) => new Date(record.timestamp).toDateString() === today)
    } catch (error) {
      console.error("AttendanceService: Error getting today attendance:", error)
      return []
    }
  }
}

export default AttendanceService 